import {Component, Input} from '@angular/core';
import {StartupService} from '../../../../service-ui/startup.service';
import {Util} from '../../../../util/Util';

/**
 * Renders a Project folder path as a readable breadcrumb chip.
 *
 * Every Projects dialog used to print the raw path inline:
 *
 * ```html
 * <span i18n="@@Parent folder">Parent folder</span><span> [</span>{{selectedFolder}}<span>]</span>
 * ```
 *
 * which produces **"Parent folder [/]"** at the tree root. That is the
 * internal spelling of "no folder", and it means nothing to anyone who
 * has not read the code — a lone `/` is not a place, and the square
 * brackets read like a placeholder that failed to fill in. Deeper in
 * the tree it is only slightly better: `[/Fiction/2026/Reprints]` is a
 * path, not a location the user recognises from the screen they were
 * just looking at.
 *
 * This renders the same information the way the rest of the app already
 * talks about it: the root is **My Projects** (the title of the Projects
 * screen and the label on the breadcrumb buttons in the Move dialogs),
 * and each level below is a chevron-separated crumb.
 *
 * The label is content-projected rather than an `@Input`, so each call
 * site keeps its own `i18n` attribute on real template text and the
 * extractor still sees it.
 *
 * Lives as a component — not a CSS class plus copied markup — because
 * view encapsulation would leave the styles behind in whichever
 * component defined them. See the note about `.clippedWidth` in
 * AGENTS.md for what that looks like when it goes wrong.
 */
@Component({
  selector: 'app-folder-path',
  templateUrl: './folder-path.component.html',
  styleUrls: ['./folder-path.component.css']
})
export class FolderPathComponent {
  /**
   * Raw folder path. `''`, `'/'` and `undefined` all mean the tree
   * root — the codebase spells it all three ways (`Project.folder`
   * defaults to `''`, `selectedFolder` starts as `'/'`).
   */
  @Input() path: string;

  constructor(public startupService: StartupService) {
  }

  /** True when this path is the tree root, however it is spelled. */
  get isRoot(): boolean {
    return Util.normalizeFolderPath(this.path || '') === '';
  }

  /** Folder names below the root, in order. */
  get segments(): string[] {
    const normalized = Util.normalizeFolderPath(this.path || '');
    if ( !normalized ) {
      return [];
    }
    return normalized.split('/').filter(s => s.length > 0);
  }

  /** Separator direction has to follow the reading direction. */
  get separatorIcon(): string {
    return this.startupService.dir === 'rtl' ? 'chevron_left' : 'chevron_right';
  }

  /** Raw path, kept on the tooltip so support can still see it. */
  get rawPath(): string {
    return this.path || '/';
  }
}
